import React from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import { getLastMeasurements, getYoungestMetrics, getRangeMetrics, isActiveDevice } from '#component/device/helper';
import measurementsIcon from '#component/icon/measurements';
import { formatter } from '#component/shipment/helper';
import DeviceEvent from './event';
import {
  TooltipWrapper,
  Divider,
  InfoCnt,
  InfoSection,
  MeasurementsCtn,
  LastLocationCtn,
  LinkStyleCtn,
  LinkStyle,
  ActiveInLink,
  LinkStyleSecond,
  GreyText,
  DividerCtn,
  ActiveInCnt
} from './styles';
import { momentFormat } from '#helper';

function DeviceInfo(props) {
  const {
    device = {},
    device: { id, lastLocation, shipments, appliances },
    account,
    meta
  } = props;

  const tabsMap = {
    map: 90
  };

  const itemsToRender = device.lastMetricSource ?
    getYoungestMetrics(device.lastMetric?.youngest, account, ['temperature', 'humidity', 'battery']) :
    device.metricsRange ? getRangeMetrics(device.metricsRange, device, account, meta.device) :
      getLastMeasurements(device.metrics, device, account, meta.device);

  const getDateRange = date => {
    const day = date.first && date.last ? 0 : 1000 * 60 * 60 * 24;
    const start = +new Date(date.first ? date.first : date) - day / 2;
    const end = +new Date(date.last ? date.last : date) + day / 2;

    return `${new Date(start).toISOString()}/${new Date(end).toISOString()}`;
  };

  const getTootlip = date => {
    const isObject = date.first && date.last;
    const start = momentFormat(date.first ? date.first : date, account, 'dateTimePartial');
    const end = momentFormat(date.last ? date.last : date, account, 'dateTimePartial');

    return isObject ? `${start} - ${end}` : start;
  };

  const dateRange = device.firstTimestamp ?
    { first: device.firstTimestamp, last: device.lastTimestamp } : null;

  const getTabUrl = (tabNum, timestamp) =>
    `/device/${id}/t/${tabNum}/dr/${getDateRange(timestamp)}`;

  const isActive = isActiveDevice(device);

  return (
    <TooltipWrapper>
      <InfoSection>
        <LastLocationCtn>
          {dateRange ?
            <Typography color="textSecondary">
              First at:&nbsp;
              <LinkStyleSecond
                to={{
                  pathname: `${getTabUrl(tabsMap.map, dateRange)}`,
                  search: 'lastlocation'
                }}
              >
                {momentFormat(device.firstTimestamp, account, 'dateTimePartial')}
              </LinkStyleSecond>
              <br />
              Last at:&nbsp;
              <LinkStyleSecond
                to={{
                  pathname: `${getTabUrl(tabsMap.map, dateRange)}`,
                  search: 'lastlocation'
                }}
              >
                {momentFormat(device.lastTimestamp, account, 'dateTimePartial')}
              </LinkStyleSecond>
            </Typography> :
            <GreyText color="textSecondary">
              Located here:&nbsp;
              <LinkStyleSecond
                to={{
                  // device.timestamp - from mapbox manually clustered
                  pathname: `${getTabUrl(tabsMap.map, lastLocation?.timestamp || device.timestamp)}`,
                  search: 'lastlocation'
                }}
              >
                {momentFormat(lastLocation?.timestamp || device.timestamp, account, 'dateTimePartial')}
              </LinkStyleSecond>
            </GreyText>}
        </LastLocationCtn>
      </InfoSection>
      <DividerCtn
        style={{ display: itemsToRender?.length ? 'block' : 'none' }}
      >
        <Divider />
      </DividerCtn>
      <InfoCnt
        style={{ display: itemsToRender?.length ? 'block' : 'none' }}
      >
        <MeasurementsCtn>
          {itemsToRender?.length ? itemsToRender.map((item, i) => (
            <Tooltip
              title={getTootlip(item.dateRange || lastLocation?.timestamp)}
              key={item.identifier}
            >
              <LinkStyleCtn
                to={{
                  pathname: getTabUrl(
                    i,
                    item.dateRange || lastLocation?.timestamp
                  )
                }}
                className={classNames({
                  alertColor: !!item.excursion,
                  lightParam: item.identifier === 'light',
                  pressureParam: item.identifier === 'pressure'
                })}
              >
                {measurementsIcon(item.identifier, device, item.value)}
                <LinkStyle>
                  {item.label ?? item.value}
                  {item.unit}
                </LinkStyle>
              </LinkStyleCtn>
            </Tooltip>
          )) : null}
        </MeasurementsCtn>
      </InfoCnt>
      <DividerCtn
        style={{ display: device.lastEvent?.identifier ? 'block' : 'none' }}
      >
        <Divider />
      </DividerCtn>
      {device.lastEvent && (
        <DeviceEvent device={device} getTabUrl={getTabUrl} />
      )}
      <DividerCtn
        style={{ display: shipments?.length || appliances?.length ? 'block' : 'none' }}
      >
        <Divider />
      </DividerCtn>
      {isActive && (
      <>
        {shipments?.length && (
        <ActiveInCnt variant="body1" component="p">
          Active in shipment
          { shipments.length > 1 ? 's' : '' }
          :&nbsp;
          { shipments.map((s, i) => (
            <React.Fragment key={`shipment${s?.uid}`}>
              <ActiveInLink
                to={`/shipment/${s.id}`}
              >
                {s.label ?? formatter(s?.uid)}
              </ActiveInLink>
              {i + 1 !== shipments.length ? ', ' : ''}
            </React.Fragment>
          ))}
        </ActiveInCnt>
        )}
        {appliances?.length && (
        <ActiveInCnt variant="body1" component="p">
          Active in application
          { appliances.length > 1 ? 's' : '' }
          :&nbsp;
          { appliances.map((s, i) => (
            <React.Fragment key={`appliance${s?.uid}`}>
              <ActiveInLink
                to={`/warehouse/appliance-overview/${s.id}`}
              >
                {s.label}
              </ActiveInLink>
              {i + 1 !== appliances.length ? ', ' : ''}
            </React.Fragment>
          ))}
        </ActiveInCnt>
        )}
      </>
      )}
    </TooltipWrapper>
  );
}

export default DeviceInfo;
